import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  Workspace,
  WorkspaceFull,
  WorkspaceLimits,
  WorkspaceService,
  WorkspaceCreateRequest,
  WorkspaceUpdateRequest,
  DeletedWorkspace,
  WorkspacePersona,
  PersonaCreateRequest,
  PersonaUpdateRequest,
  PersonaGenerateRequest,
} from '@/types';
import api from '@/services/api';
import { abortScope, scopedRequest, StaleIdentityError } from '@/features/a0/identity/identity';
import { abortActiveStream } from './postsStore';

let personasRequestFor: string | null = null;

/** Cancel the previous workspace's requests and generation stream before switching away from it. */
const leaveWorkspace = (currentId: string | undefined, nextId: string | undefined) => {
  if (currentId === nextId) return;
  abortScope('workspace');
  abortActiveStream();
};

/** Tabs of the in-tool workspace editor (WorkspaceEditModal). */
export type WorkspaceEditorTab = 'profile' | 'services' | 'personas' | 'voice' | 'facts' | 'harvest';

interface WorkspaceState {
  // State
  workspaces: Workspace[];
  activeWorkspace: WorkspaceFull | null;
  limits: WorkspaceLimits | null;
  deletedWorkspaces: DeletedWorkspace[];
  isLoading: boolean;
  error: string | null;

  // Which tab of the in-tool workspace editor is open (null = closed).
  // Kept in the store so anything in the tool can open it — a post
  // card's "none of these options use one of your real facts" chip now sends the user straight
  // to the Fact Bank, instead of naming a gap the tool gave them no way to close.
  editorTab: WorkspaceEditorTab | null;

  // Persona state
  personas: WorkspacePersona[];
  isLoadingPersonas: boolean;
  selectedPersonaIds: string[];

  // Actions
  openEditorTab: (tab: WorkspaceEditorTab) => void;
  closeEditor: () => void;
  setWorkspaces: (workspaces: Workspace[]) => void;
  setActiveWorkspace: (workspace: WorkspaceFull | null) => void;
  setLimits: (limits: WorkspaceLimits | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // API Actions
  fetchWorkspaces: () => Promise<void>;
  fetchActiveWorkspace: () => Promise<void>;
  fetchLimits: () => Promise<void>;
  fetchDeletedWorkspaces: () => Promise<void>;

  createWorkspace: (data: WorkspaceCreateRequest) => Promise<Workspace>;
  updateWorkspace: (id: string, data: WorkspaceUpdateRequest) => Promise<Workspace>;
  deleteWorkspace: (id: string) => Promise<void>;
  activateWorkspace: (id: string) => Promise<void>;
  restoreWorkspace: (id: string) => Promise<void>;

  // Service actions
  fetchServices: (workspaceId: string) => Promise<WorkspaceService[]>;
  createService: (workspaceId: string, name: string, description?: string) => Promise<WorkspaceService>;
  updateService: (workspaceId: string, serviceId: string, name: string, description?: string) => Promise<WorkspaceService>;
  deleteService: (workspaceId: string, serviceId: string) => Promise<void>;
  reorderServices: (workspaceId: string, serviceIds: string[]) => Promise<void>;

  // Persona actions
  fetchPersonas: (workspaceId: string) => Promise<void>;
  generatePersonas: (workspaceId: string, data: PersonaGenerateRequest) => Promise<WorkspacePersona[]>;
  createPersona: (workspaceId: string, data: PersonaCreateRequest) => Promise<WorkspacePersona>;
  updatePersona: (workspaceId: string, personaId: string, data: PersonaUpdateRequest) => Promise<WorkspacePersona>;
  deletePersona: (workspaceId: string, personaId: string) => Promise<void>;
  setPrimaryPersona: (workspaceId: string, personaId: string) => Promise<void>;
  setSelectedPersonaIds: (personaIds: string[]) => void;

  // Company Voice actions
  fetchVoiceSettings: (workspaceId: string) => Promise<import('@/types/workspace').CompanyVoiceResponse>;
  updateVoiceSettings: (workspaceId: string, data: import('@/types/workspace').CompanyVoiceSettings) => Promise<import('@/types/workspace').CompanyVoiceResponse>;
  analyzeVoice: (workspaceId: string) => Promise<import('@/types/workspace').CompanyVoiceResponse>;

  // Company research - returns research data including suggested services
  researchCompany: (workspaceId: string, websiteUrl: string, contentUrl?: string, manualDescription?: string) => Promise<import('@/types').WorkspaceResearchResponse | void>;

  // Clear
  clearWorkspaces: () => void;
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      workspaces: [],
      activeWorkspace: null,
      limits: null,
      deletedWorkspaces: [],
      isLoading: false,
      error: null,
      editorTab: null,
      personas: [],
      isLoadingPersonas: false,
      selectedPersonaIds: [],

      openEditorTab: (tab) => set({ editorTab: tab }),
      closeEditor: () => set({ editorTab: null }),

      setWorkspaces: (workspaces) => set({ workspaces }),
      setActiveWorkspace: (workspace) => {
        leaveWorkspace(get().activeWorkspace?.id, workspace?.id);
        set({ activeWorkspace: workspace });
      },
      setLimits: (limits) => set({ limits }),
      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),

      fetchWorkspaces: async () => {
        set({ isLoading: true, error: null });
        try {
          const workspaces = await api.getWorkspaces();
          set({ workspaces: workspaces.filter(w => !w.deleted_at), isLoading: false });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to fetch workspaces';
          set({ error: message, isLoading: false });
        }
      },

      fetchActiveWorkspace: async () => {
        try {
          const workspace = await api.getActiveWorkspace();
          if (workspace) {
            set({ activeWorkspace: workspace });
          } else {
            // No active workspace — fetch workspace list and auto-select the first one
            const allWorkspaces = await api.getWorkspaces();
            if (allWorkspaces.length > 0) {
              try {
                await api.activateWorkspace(allWorkspaces[0].id);
                const full = await api.getWorkspace(allWorkspaces[0].id);
                set({ activeWorkspace: full, workspaces: allWorkspaces });
              } catch {
                console.error('Failed to auto-activate first workspace');
              }
            }
          }
        } catch (error) {
          console.error('Failed to fetch active workspace:', error);
        }
      },

      fetchLimits: async () => {
        try {
          const limits = await api.getWorkspaceLimits();
          set({ limits });
        } catch (error) {
          console.error('Failed to fetch workspace limits:', error);
        }
      },

      fetchDeletedWorkspaces: async () => {
        try {
          const deleted = await api.getDeletedWorkspaces();
          set({ deletedWorkspaces: deleted });
        } catch (error) {
          console.error('Failed to fetch deleted workspaces:', error);
        }
      },

      createWorkspace: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const workspace = await api.createWorkspace(data);
          const { workspaces } = get();
          set({ workspaces: [...workspaces, workspace], isLoading: false });
          // Refresh limits
          await get().fetchLimits();
          return workspace;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to create workspace';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      updateWorkspace: async (id, data) => {
        set({ isLoading: true, error: null });
        try {
          const updated = await api.updateWorkspace(id, data);
          const { workspaces, activeWorkspace } = get();
          set({
            workspaces: workspaces.map(w => w.id === id ? updated : w),
            activeWorkspace: activeWorkspace?.id === id 
              ? { ...activeWorkspace, ...updated }
              : activeWorkspace,
            isLoading: false,
          });
          return updated;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to update workspace';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      deleteWorkspace: async (id) => {
        set({ isLoading: true, error: null });
        try {
          await api.deleteWorkspace(id);
          const { workspaces, activeWorkspace } = get();
          const remaining = workspaces.filter(w => w.id !== id);
          set({ workspaces: remaining, isLoading: false });
          
          // If deleted was active, switch to another
          if (activeWorkspace?.id === id && remaining.length > 0) {
            await get().activateWorkspace(remaining[0].id);
          } else if (activeWorkspace?.id === id) {
            // The last workspace is gone: the server cleared the active one too.
            set({ activeWorkspace: null });
          }
          
          // Refresh limits and deleted list
          await get().fetchLimits();
          await get().fetchDeletedWorkspaces();
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to delete workspace';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      activateWorkspace: async (id) => {
        try {
          await api.activateWorkspace(id);
          // Fetch full workspace details
          const workspace = await api.getWorkspace(id);
          leaveWorkspace(get().activeWorkspace?.id, workspace.id);
          set({ activeWorkspace: workspace, selectedPersonaIds: [] });
          // Tool.tsx useEffect handles fetchPersonas when activeWorkspace changes
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to activate workspace';
          set({ error: message });
          throw error;
        }
      },

      restoreWorkspace: async (id) => {
        set({ isLoading: true, error: null });
        try {
          const restored = await api.restoreWorkspace(id);
          if (!restored || !restored.id) {
            throw new Error('Restore response was missing the workspace');
          }
          const { workspaces, deletedWorkspaces } = get();
          // Rebuild both arrays immutably from scratch (filter, don't splice/index-write)
          // so neither list can ever hold an undefined/holed entry after a restore.
          set({
            workspaces: [...workspaces.filter(Boolean), restored],
            deletedWorkspaces: deletedWorkspaces.filter(w => Boolean(w) && w.id !== id),
            isLoading: false,
          });
          // Refresh limits
          await get().fetchLimits();
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to restore workspace';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      // Service actions
      fetchServices: async (workspaceId) => {
        const services = await scopedRequest('workspace', () => api.getWorkspaceServices(workspaceId));
        const { activeWorkspace } = get();
        if (activeWorkspace?.id === workspaceId) {
          set({ activeWorkspace: { ...activeWorkspace, services } });
        }
        return services;
      },

      createService: async (workspaceId, name, description) => {
        try {
          const service = await api.createWorkspaceService(workspaceId, { name, description });
          const { activeWorkspace } = get();
          if (activeWorkspace?.id === workspaceId) {
            // Ensure services array exists before spreading
            const currentServices = activeWorkspace.services || [];
            set({
              activeWorkspace: {
                ...activeWorkspace,
                services: [...currentServices, service],
              },
            });
          }
          return service;
        } catch (error: any) {
          // Re-throw with proper error structure so caller can extract details
          throw error;
        }
      },

      updateService: async (workspaceId, serviceId, name, description) => {
        const updated = await api.updateWorkspaceService(workspaceId, serviceId, { name, description });
        const { activeWorkspace } = get();
        if (activeWorkspace?.id === workspaceId) {
          set({
            activeWorkspace: {
              ...activeWorkspace,
              services: activeWorkspace.services.map(s => s.id === serviceId ? updated : s),
            },
          });
        }
        return updated;
      },

      deleteService: async (workspaceId, serviceId) => {
        await api.deleteWorkspaceService(workspaceId, serviceId);
        const { activeWorkspace } = get();
        if (activeWorkspace?.id === workspaceId) {
          set({
            activeWorkspace: {
              ...activeWorkspace,
              services: activeWorkspace.services.filter(s => s.id !== serviceId),
            },
          });
        }
      },

      reorderServices: async (workspaceId, serviceIds) => {
        const reordered = await api.reorderWorkspaceServices(workspaceId, serviceIds);
        const { activeWorkspace } = get();
        if (activeWorkspace?.id === workspaceId) {
          set({
            activeWorkspace: {
              ...activeWorkspace,
              services: reordered,
            },
          });
        }
      },

      // Persona actions
      fetchPersonas: async (workspaceId) => {
        // Prevent concurrent fetches for the same workspace; a switch may start the next one.
        if (personasRequestFor === workspaceId) return;
        personasRequestFor = workspaceId;
        set({ isLoadingPersonas: true });
        try {
          const response = await scopedRequest('workspace', () => api.getPersonas(workspaceId));
          set({ personas: response.personas });
          // Auto-select primary persona if none selected
          const { selectedPersonaIds } = get();
          if (selectedPersonaIds.length === 0) {
            const primary = response.personas.find(p => p.is_primary);
            if (primary) {
              set({ selectedPersonaIds: [primary.id] });
            }
          }
        } catch (error) {
          // Don't clear existing personas on error — keep whatever is in store
          if (!(error instanceof StaleIdentityError)) console.error('Failed to fetch personas:', error);
        } finally {
          if (personasRequestFor === workspaceId) {
            personasRequestFor = null;
            set({ isLoadingPersonas: false });
          }
        }
      },

      generatePersonas: async (workspaceId, data) => {
        set({ isLoadingPersonas: true });
        try {
          const response = await api.generatePersonas(workspaceId, data);
          set({ personas: response.personas, isLoadingPersonas: false });
          // Select primary
          const primary = response.personas.find(p => p.is_primary);
          if (primary) {
            set({ selectedPersonaIds: [primary.id] });
          }
          return response.personas;
        } catch (error) {
          set({ isLoadingPersonas: false });
          throw error;
        }
      },

      createPersona: async (workspaceId, data) => {
        const persona = await api.createPersona(workspaceId, data);
        const { personas } = get();
        // If new persona is primary, unset others locally
        const updated = data.is_primary
          ? personas.map(p => ({ ...p, is_primary: false }))
          : personas;
        set({ personas: [...updated, persona] });
        return persona;
      },

      updatePersona: async (workspaceId, personaId, data) => {
        const updated = await api.updatePersona(workspaceId, personaId, data);
        const { personas } = get();
        let newPersonas = personas.map(p => p.id === personaId ? updated : p);
        // If updated to primary, unset others
        if (data.is_primary) {
          newPersonas = newPersonas.map(p =>
            p.id === personaId ? p : { ...p, is_primary: false }
          );
        }
        set({ personas: newPersonas });
        return updated;
      },

      deletePersona: async (workspaceId, personaId) => {
        await api.deletePersona(workspaceId, personaId);
        const { personas, selectedPersonaIds } = get();
        const remaining = personas.filter(p => p.id !== personaId);
        const updates: Partial<WorkspaceState> = { personas: remaining };
        if (selectedPersonaIds.includes(personaId)) {
          updates.selectedPersonaIds = selectedPersonaIds.filter(id => id !== personaId);
        }
        set(updates as any);
      },

      setPrimaryPersona: async (workspaceId, personaId) => {
        await api.setPrimaryPersona(workspaceId, personaId);
        const { personas } = get();
        set({
          personas: personas.map(p => ({
            ...p,
            is_primary: p.id === personaId,
          })),
        });
      },

      setSelectedPersonaIds: (personaIds) => {
        set({ selectedPersonaIds: personaIds });
      },

      // Company Voice
      fetchVoiceSettings: async (workspaceId) => {
        const voice = await api.getCompanyVoice(workspaceId);
        return voice;
      },

      updateVoiceSettings: async (workspaceId, data) => {
        const voice = await api.updateCompanyVoice(workspaceId, data);
        return voice;
      },

      analyzeVoice: async (workspaceId) => {
        const result = await api.analyzeCompanyVoice(workspaceId);
        return result;
      },

      researchCompany: async (workspaceId, websiteUrl, contentUrl, manualDescription) => {
        set({ isLoading: true, error: null });
        try {
          const research = await api.researchWorkspaceCompany(workspaceId, websiteUrl, contentUrl, manualDescription);

          // Research data is NOT saved to the workspace DB — the user must click
          // "Save Profile" to commit. We only create suggested services here since
          // they are additive and don't overwrite existing workspace data.
          let servicesCreated = false;
          if (research.suggested_services && research.suggested_services.length > 0) {
            for (const suggestion of research.suggested_services) {
              try {
                await get().createService(workspaceId, suggestion.name, suggestion.description);
                servicesCreated = true;
              } catch {
                // Ignore duplicate service errors, continue with others
              }
            }
          }

          // Only refresh workspace if new services were added (to update services list in store)
          if (servicesCreated) {
            await get().fetchActiveWorkspace();
          }

          set({ isLoading: false });
          return research;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to research company';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      clearWorkspaces: () => {
        personasRequestFor = null;
        set({
          workspaces: [],
          activeWorkspace: null,
          limits: null,
          deletedWorkspaces: [],
          isLoading: false,
          error: null,
          personas: [],
          isLoadingPersonas: false,
          selectedPersonaIds: [],
        });
      },
    }),
    {
      name: 'workspace-storage',
      partialize: (state) => ({
        // Only persist minimal data
        activeWorkspaceId: state.activeWorkspace?.id,
      }),
    }
  )
);
